import { useEffect, useState } from "react";
import api from "@/lib/api";
import { REPRISE_BRANDS, REPRISE_CHOICES, REPRISE_DIMS, REPRISE_EXTRAS, REPRISE_TEXT } from "@/components/RepriseBrand";

export const HTP_URL = "https://www.horsetrailerprice.com";
export const HTP_ORIGINS = ["https://www.horsetrailerprice.com", "https://horsetrailerprice.com"];
export const HTP_TYPES = ["van", "fourgon"];
const CHOICE_LABELS = Object.fromEntries(REPRISE_CHOICES.map(([k, l]) => [k, l]));
const STATE_KEYS = REPRISE_CHOICES.map(([k]) => k).filter((k) => !["places", "essieux"].includes(k));
const TEXT_KEYS = [...REPRISE_TEXT.map(([k]) => k), ...REPRISE_DIMS.map(([k]) => k)];

const val = (o) => (typeof o === "string" || typeof o === "number" ? String(o) : String(o?.valeur ?? o?.value ?? o?.libelle ?? o?.label ?? ""));
const list = (x) => {
  if (Array.isArray(x)) return x.map(val).filter(Boolean);
  if (x && typeof x === "object") return list(x.options ?? x.valeurs ?? x.values ?? null);
  return null;
};
const scopeOf = (p, types) => {
  const s = Array.isArray(p) ? p : String(p || "").toLowerCase().split(/[,|/ ]+/);
  const out = s.filter((x) => types.includes(x));
  return out.length ? out : types;
};

const FALLBACK = {
  live: false,
  types: HTP_TYPES,
  brands: REPRISE_BRANDS,
  places: REPRISE_CHOICES.find(([k]) => k === "places")[2],
  essieux: REPRISE_CHOICES.find(([k]) => k === "essieux")[2],
  states: Object.fromEntries(HTP_TYPES.map((t) => [t, Object.fromEntries(REPRISE_CHOICES.filter(([k, , , only]) => STATE_KEYS.includes(k) && (!only || only === t)).map(([k, , o]) => [k, o]))])),
  extras: REPRISE_EXTRAS.map(([key, label, scope]) => ({ key, label, scope })),
};

// Tolerant reader of GET /api/integration/criteres; anything missing keeps the local list
export const parseCriteria = (d) => {
  if (!d) return FALLBACK;
  const types = (list(d.types) || []).filter((x) => HTP_TYPES.includes(x));
  const T = types.length ? types : HTP_TYPES;
  const crit = d.criteres || d.criteres_etat || d.etats || {};
  const extrasRaw = Array.isArray(d.options_supplementaires) ? d.options_supplementaires : (Array.isArray(d.options) ? d.options : null);
  const extrasObj = !extrasRaw && d.options && typeof d.options === "object" ? Object.entries(d.options).map(([key, o]) => ({ key, ...(typeof o === "object" ? o : { label: o }) })) : null;
  const ex = (extrasRaw || extrasObj || []).map((o) => (typeof o === "string" ? { key: o } : o)).map((o) => {
    const key = o.cle || o.key || o.code || o.nom;
    const fb = REPRISE_EXTRAS.find(([k]) => k === key);
    return key ? { key, label: o.libelle || o.label || (fb ? fb[1] : key), scope: scopeOf(o.portee ?? o.scope ?? o.types, T) } : null;
  }).filter(Boolean);
  return {
    live: true,
    types: T,
    brands: Object.fromEntries(T.map((t) => [t, list(d.marques?.[t]) || REPRISE_BRANDS[t] || []])),
    places: list(d.places) || FALLBACK.places,
    essieux: list(d.essieux) || FALLBACK.essieux,
    states: Object.fromEntries(T.map((t) => [t, crit[t]
      ? Object.fromEntries(STATE_KEYS.map((k) => [k, list(crit[t][k])]).filter(([, v]) => v && v.length))
      : FALLBACK.states[t]])),
    extras: ex.length ? ex : FALLBACK.extras,
  };
};

let critPromise = null;
export const useHtpCriteria = () => {
  const [crit, setCrit] = useState(FALLBACK);
  useEffect(() => {
    critPromise = critPromise || api.get("/htp/criteres").then(({ data }) => parseCriteria(data?.live ? data.data : null)).catch(() => { critPromise = null; return FALLBACK; });
    let alive = true;
    critPromise.then((c) => { if (alive) setCrit({ ...c, ready: true }); });
    return () => { alive = false; };
  }, []);
  return crit;
};

export const stateLabel = (k) => CHOICE_LABELS[k] || k;
// « Refait à neuf » only for a wooden floor
export const stateOptions = (crit, type, k, ti) => {
  const o = (crit.states[type] || {})[k] || [];
  return k === "plancher" && ti.plancher_type !== "Bois" ? o.filter((x) => x !== "Refait à neuf") : o;
};
export const extrasFor = (crit, type) => crit.extras.filter((e) => e.scope.includes(type));

// HTP Espace pro estimation URL: only the filled fields, every value encoded
export const buildEstimateUrl = (ti, client, token, crit) => {
  const type = ti.type;
  const p = [];
  const add = (k, v) => { if (v !== undefined && v !== null && String(v).trim() !== "") p.push([k, String(v).trim()]); };
  if (crit.types.includes(type)) add("type", type);
  add("marque", ti.marque); add("modele", ti.modele); add("annee", ti.annee); add("immat", ti.immat); add("ptac", ti.ptac);
  if (type === "van") add("places", ti.places);
  if (type !== "van") add("essieux", ti.essieux);
  if (type === "fourgon") REPRISE_DIMS.forEach(([k]) => add(k, ti[k]));
  Object.keys(crit.states[type] || {}).forEach((k) => add(k, ti[k]));
  extrasFor(crit, type).forEach((e) => { if (ti[e.key]) add(e.key, "1"); });
  p.push(["tva_recuperable", ti.tvaRecup ? "1" : "0"]);
  add("client_nom", [client.firstname, client.name].filter((x) => (x || "").trim()).join(" "));
  add("client_email", client.email);
  add("client_tel", (client.phone || "").trim() ? (/^\s*(\+|00)/.test(client.phone) ? client.phone : `${client.phone_code || ""} ${client.phone}`) : "");
  return `${HTP_URL}/pro/estimation?retour=devis&sso=${encodeURIComponent(token)}&${p.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&")}`;
};

const truthy = (v) => v === true || v === 1 || ["1", "true", "oui", "yes", "on"].includes(String(v).toLowerCase());
const num = (v) => { const n = Number(String(v ?? "").replace(",", ".")); return Number.isFinite(n) && n > 0 ? n : 0; };

// HTP payload ({fiche, client, montant_ttc, ...}) -> patch for one trade-in; unknown or empty values are ignored
export const htpToReprise = (msg, crit, vat) => {
  const f = msg.fiche || {};
  const type = HTP_TYPES.includes(f.type) && crit.types.includes(f.type) ? f.type : null;
  const patch = type ? { type } : {};
  const ty = type || "van";
  const s = (v) => (v === undefined || v === null ? "" : String(v).trim());
  if (s(f.marque)) { patch.marque = s(f.marque); patch.marqueAutre = !(crit.brands[ty] || []).includes(patch.marque); }
  TEXT_KEYS.forEach((k) => { if (s(f[k]) && (k !== "annee" || /^\d{4}$/.test(s(f[k])))) patch[k] = s(f[k]); });
  if (ty === "van" && crit.places.includes(s(f.places))) patch.places = s(f.places);
  if (ty !== "van" && crit.essieux.includes(s(f.essieux))) patch.essieux = s(f.essieux);
  Object.entries(crit.states[ty] || {}).forEach(([k, opts]) => { if (opts.includes(s(f[k]))) patch[k] = s(f[k]); });
  if (patch.plancher === "Refait à neuf" && (patch.plancher_type || f.plancher_type) !== "Bois") delete patch.plancher;
  extrasFor(crit, ty).forEach((e) => { if (s(f[e.key]) !== "") patch[e.key] = truthy(f[e.key]) ? "1" : ""; });
  const tvaRaw = msg.tva_recuperable ?? f.tva_recuperable;
  if (tvaRaw !== undefined && tvaRaw !== "") patch.tvaRecup = truthy(tvaRaw);
  const ttc = num(msg.montant_ttc);
  const ht = num(msg.prix_reprise_ht);
  if (ttc) patch.montant = Math.round(ttc * 100) / 100;
  else if (ht) patch.montant = Math.round(ht * (patch.tvaRecup ? 1 + (vat || 0.2) : 1) * 100) / 100;
  if (num(msg.prix_saisi)) patch.prix_saisi = String(num(msg.prix_saisi));
  patch.htp = {
    estimation_id: s(msg.estimation_id), montant_ttc: ttc, prix_reprise_ht: ht,
    prix_revendeur_ttc: num(msg.prix_revendeur_ttc), prix_client_ttc: num(msg.prix_client_ttc), prix_saisi: num(msg.prix_saisi),
  };
  const photos = (Array.isArray(msg.photos) ? msg.photos : []).map((u) => (typeof u === "string" ? u : u?.url)).filter((u) => /^https:\/\//.test(u || ""));
  return { patch, photos };
};

export const htpToClient = (c = {}) => {
  const out = {};
  const nom = String(c.nom || "").trim();
  if (nom) {
    const parts = nom.split(/\s+/);
    if (parts.length > 1) { out.firstname = parts[0]; out.name = parts.slice(1).join(" "); } else out.name = nom;
  }
  if (String(c.email || "").trim()) out.email = String(c.email).trim();
  if (String(c.tel || "").trim()) {
    const tel = String(c.tel).trim();
    if (/^(\+|00)/.test(tel)) { out.phone = tel; out.phone_code = ""; } else out.phone = tel;
  }
  return out;
};

// /nouveau-devis query string -> same shape as the postMessage payload
export const paramsToHtp = (sp) => {
  const g = (k) => sp.get(k) ?? "";
  const fiche = {};
  sp.forEach((v, k) => { if (!k.startsWith("client_") && !["source", "montant_ttc", "prix_reprise_ht", "prix_revendeur_ttc", "prix_client_ttc", "prix_saisi", "estimation_id", "photos"].includes(k)) fiche[k] = v; });
  return {
    type: "fautras-reprise", fiche, tva_recuperable: g("tva_recuperable"),
    client: { nom: g("client_nom"), email: g("client_email"), tel: g("client_tel") },
    montant_ttc: g("montant_ttc"), prix_reprise_ht: g("prix_reprise_ht"), prix_revendeur_ttc: g("prix_revendeur_ttc"),
    prix_client_ttc: g("prix_client_ttc"), prix_saisi: g("prix_saisi"), estimation_id: g("estimation_id"),
    photos: sp.getAll("photos"),
  };
};
